import type { ReactNode } from "react";
import { Link } from "react-router";
import { Badge, DataTable, Money, type DataTableColumn } from "@/components";
import { formatDate } from "@/lib/format";
import type { SupplierOrder } from "../api";
import { ORDER_STATUS_LOOK, orderPath } from "../lotDisplay";
import styles from "./OrdersTable.module.css";

/** Supplier orders with how much of each has been received; each row opens the order, or with `linkTo`
 * somewhere else (the receive page opens the walk through the parcel). */
export function OrdersTable({
  orders,
  caption,
  loading,
  error,
  onRetry,
  empty,
  linkTo = (order) => orderPath(order.id),
}: {
  orders: SupplierOrder[] | undefined;
  caption: string;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  empty?: ReactNode;
  linkTo?: (order: SupplierOrder) => string;
}) {
  const columns: DataTableColumn<SupplierOrder>[] = [
    {
      id: "order",
      header: "Order",
      width: "wide",
      cell: (order) => (
        <span className={styles.orderCell}>
          <Link to={linkTo(order)} className={styles.orderLink}>
            {order.supplier_name || "Supplier"}
            {order.reference ? <span className={styles.reference}> {order.reference}</span> : null}
          </Link>
          <span className={styles.secondary}>Ordered {formatDate(order.ordered_at)}</span>
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      width: "fit",
      cell: (order) => {
        const look = ORDER_STATUS_LOOK[order.status];
        return (
          <Badge tone={look.tone} icon={look.icon}>
            {look.label}
          </Badge>
        );
      },
    },
    {
      id: "received",
      header: "Received",
      numeric: true,
      width: "fit",
      cell: (order) => `${order.received_count} of ${order.lot_count}`,
    },
    {
      id: "total",
      header: "Total",
      numeric: true,
      width: "fit",
      cell: (order) => <Money minor={order.total_cost_minor} currency={order.currency || undefined} />,
    },
  ];
  return (
    <DataTable
      caption={caption}
      columns={columns}
      rows={orders}
      getRowId={(order) => order.id}
      loading={loading}
      error={error}
      onRetry={onRetry}
      empty={empty}
    />
  );
}
